import { CheckCircle, Phone, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/ui-custom/SEO';

const cities = [
  {
    name: 'Dothan',
    county: 'Houston County',
    path: '/service-areas/dothan-al',
    description: 'Our primary service hub — Dothan is the largest city in the Wiregrass and home to the majority of our residential and commercial customers.',
  },
  {
    name: 'Enterprise',
    county: 'Coffee County',
    path: '/service-areas/enterprise-al',
    description: 'Fast, reliable plumbing and electrical service for Enterprise homes and businesses throughout Coffee County.',
  },
  {
    name: 'Ozark',
    county: 'Dale County',
    path: '/service-areas/ozark-al',
    description: 'Serving Ozark and the surrounding Dale County area with professional plumbing and electrical repairs.',
  },
  {
    name: 'Newton',
    county: 'Dale County',
    path: '/service-areas/newton-al',
    description: 'Our home base. Ard\'s Plumbing & Electric has been serving Newton and Dale County from our Hwy 92 location for over 65 years.',
  },
  {
    name: 'Geneva',
    county: 'Geneva County',
    path: '/service-areas/geneva-al',
    description: 'Trusted plumbing and electrical service throughout Geneva and Geneva County.',
  },
  {
    name: 'Headland',
    county: 'Henry County',
    path: '/service-areas/headland-al',
    description: 'Professional plumbing and electrical services for Headland and Henry County residents and businesses.',
  },
  {
    name: 'Daleville',
    county: 'Dale County',
    path: '/service-areas/daleville-al',
    description: 'Serving Daleville and the Fort Novosel community with licensed, insured plumbing and electrical work.',
  },
  {
    name: 'Midland City',
    county: 'Dale County',
    path: '/service-areas/midland-city-al',
    description: 'Reliable home and business plumbing and electrical service for Midland City and the surrounding area.',
  },
  {
    name: 'Rehobeth',
    county: 'Houston County',
    path: '/service-areas/rehobeth-al',
    description: 'Fast response plumbing and electrical service for Rehobeth and the greater Houston County area.',
  },
];

const ServiceAreas = () => {
  return (
    <>
      <SEO
        title="Service Areas | Plumber & Electrician in the Wiregrass AL"
        description="Ard's Plumbing & Electric serves Dothan, Enterprise, Ozark, Newton, Geneva, Headland, Daleville, Midland City, and Rehobeth, AL. Call (334) 692-5537."
        keywords="plumber Wiregrass Alabama, electrician Southeast Alabama, plumbing service areas Alabama, Dothan Enterprise Ozark plumber"
        canonical="/service-areas"
        additionalSchema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          'itemListElement': [
            { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://ardsdothan.com/' },
            { '@type': 'ListItem', 'position': 2, 'name': 'Service Areas', 'item': 'https://ardsdothan.com/service-areas' },
          ],
        }}
      />

      <section className="bg-brand-blue pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 text-white text-sm px-4 py-2 rounded-full mb-6">
            <MapPin className="w-4 h-4" />
            Southeast Alabama
          </div>
          <h1 className="font-heading font-black text-4xl lg:text-6xl text-white leading-tight mb-6">
            SERVICE AREAS<br />
            <span className="text-brand-yellow">WIREGRASS, AL</span>
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-8">
            Ard's Plumbing & Electric serves the entire Southeast Alabama Wiregrass region.
            Licensed, insured, and locally based in Newton, AL for over 65 years.
          </p>
          <a
            href="tel:3346925537"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-brand-coral text-white font-semibold text-lg hover:bg-brand-coral/90 transition-colors"
          >
            <Phone className="w-5 h-5" />
            (334) 692-5537
          </a>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading font-black text-3xl text-brand-black mb-4">
              CITIES WE SERVE
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              From Dothan to Enterprise, Ozark to Geneva — select your city below to learn more about our services in your area.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cities.map(({ name, county, path, description }) => (
              <Link
                key={name}
                to={path}
                className="bg-gray-50 rounded-3xl p-8 border border-gray-200 hover:border-brand-blue hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 bg-brand-blue/10 rounded-full flex items-center justify-center group-hover:bg-brand-blue/20 transition-colors">
                    <MapPin className="w-4 h-4 text-brand-blue" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg text-brand-black">{name}, AL</h3>
                    <p className="text-xs text-gray-500">{county}</p>
                  </div>
                </div>
                <p className="text-sm text-gray-600 mb-4">{description}</p>
                <span className="text-sm font-semibold text-brand-blue group-hover:underline">
                  View services in {name} →
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-black text-3xl text-brand-black mb-6">
                ABOUT OUR SERVICE AREA
              </h2>
              <p className="text-gray-700 mb-4">
                The Wiregrass region of Southeast Alabama is our home. Ard's Plumbing & Electric
                has been serving this community from our Newton, AL location for over 65 years —
                through generations of homeowners and business owners who've trusted us with
                their most important systems.
              </p>
              <p className="text-gray-700 mb-6">
                Whether you're in downtown Dothan or a rural property in Geneva County, we treat
                every service call with the same care and professionalism. No job is too small,
                and no location is too far — if you're in the Wiregrass, we're your plumber and
                electrician.
              </p>
              <ul className="space-y-3">
                {[
                  'Licensed & insured in Alabama',
                  '65+ years serving the Wiregrass',
                  'Mon–Fri 8AM–5PM service hours',
                  'Residential & commercial service',
                  'Fast, same-day response when available',
                ].map((item) => (
                  <li key={item} className="flex items-center gap-3 text-gray-700">
                    <CheckCircle className="w-5 h-5 text-brand-blue flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-gray-200">
              <h3 className="font-heading font-bold text-xl text-brand-black mb-4">
                NOT SURE IF WE SERVE YOUR AREA?
              </h3>
              <p className="text-gray-700 mb-6">
                Give us a call and we'll let you know right away. We're always happy to help — even if it's just pointing you in the right direction.
              </p>
              <a
                href="tel:3346925537"
                className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-2xl bg-brand-blue text-white font-bold text-lg hover:bg-brand-blue/90 transition-colors"
              >
                <Phone className="w-5 h-5" />
                Call (334) 692-5537
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default ServiceAreas;
